import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import bobPic from '../public/images/bob update.jpeg'

export default function Home(): JSX.Element {
  return (
    <div className="min-h-screen flex flex-col">
      <Head>
        <title>
          Robert Karasov, M.D. — Minneapolis Mohel · Jewish Ritual Circumcision
        </title>
        <meta
          content="Robert (Bob) Karasov, M.D. is a pediatrician and Mohel based in Minneapolis–St. Paul, performing Brit Milah ceremonies for families across all Jewish denominations."
          name="description"
        />
      </Head>

      <TopNav />

      <main className="flex-1">
        <Hero />
        <CardRow />
        <QuoteBand />
      </main>

      <SiteFooter />
    </div>
  )
}

const TopNav = (): JSX.Element => {
  return (
    <header className="border-b border-hairline bg-cream/80 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <Link className="font-serif text-lg text-ink" href="/">
          Robert Karasov, <span className="italic text-petrol">M.D.</span>
        </Link>
        <nav className="md:hidden">
          <ul className="flex items-center gap-7 text-sm text-ink-muted">
            <NavLink href="/ceremony">The Ceremony</NavLink>
            <NavLink href="/need">What You Need</NavLink>
            <NavLink href="/tradition">Tradition</NavLink>
            <NavLink href="/testimonials">Testimonials</NavLink>
            <li>
              <Link
                className="rounded-md bg-petrol px-4 py-2 text-sm font-medium text-cream transition hover:bg-petrol-dark"
                href="/contact"
              >
                Contact
              </Link>
            </li>
          </ul>
        </nav>
        <Link
          className="hidden md:inline-block rounded-md bg-petrol px-3 py-2 text-xs font-medium text-cream"
          href="/contact"
        >
          Contact
        </Link>
      </div>
    </header>
  )
}

const NavLink = ({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}): JSX.Element => {
  return (
    <li>
      <Link className="transition hover:text-petrol" href={href}>
        {children}
      </Link>
    </li>
  )
}

const Hero = (): JSX.Element => {
  return (
    <section className="mx-auto max-w-5xl px-6 pt-16 pb-20 md:pt-10 md:pb-12">
      <div className="grid grid-cols-2 items-center gap-12 md:grid-cols-1 md:gap-8">
        <div>
          <p className="mb-5 inline-block rounded-full border border-hairline bg-surface px-3 py-1 text-xs uppercase tracking-widest text-sage">
            Minneapolis · Saint Paul
          </p>
          <h1 className="font-serif text-5xl leading-[1.05] tracking-tight text-ink sm:text-4xl">
            A warm, thoughtful{' '}
            <span className="italic text-petrol">Brit Milah</span> for your
            family.
          </h1>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-muted">
            I'm Bob Karasov — a pediatrician and Mohel serving the Twin Cities
            since 1985. I have performed well over a thousand brises with Jewish
            families and clergy from every denomination.
          </p>
          <div className="mt-8">
            <Link
              className="rounded-md bg-petrol px-5 py-3 text-sm font-medium text-cream transition hover:bg-petrol-dark"
              href="/contact"
            >
              Get in touch
            </Link>
          </div>
        </div>

        <div>
          <div className="relative mx-auto aspect-[4/5] w-full max-w-sm overflow-hidden rounded-2xl border border-hairline bg-surface shadow-card">
            <Image
              alt="Bob Karasov, M.D."
              className="object-cover"
              fill
              priority
              sizes="(max-width: 768px) 80vw, 400px"
              src={bobPic}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

const CardRow = (): JSX.Element => {
  const tints = ['bg-petrol', 'bg-sage', 'bg-gold'] as const
  const cards = [
    {
      href: '/home',
      kicker: 'Meet the Mohel',
      title: 'About',
      blurb:
        'A pediatrician since 1985, Mohel since 1985, observant Jew and lay Cantor — a short introduction.',
    },
    {
      href: '/ceremony',
      kicker: 'The day itself',
      title: 'The Ceremony',
      blurb:
        'A clear walk-through of what to expect on the day, from blessings to the celebratory meal.',
    },
    {
      href: '/need',
      kicker: 'Before the bris',
      title: 'What You Need',
      blurb:
        'A simple checklist for the household — items to gather, plus answers to common first-time questions.',
    },
    {
      href: '/tradition',
      kicker: 'Roots',
      title: 'History &amp; Traditions',
      blurb:
        'The 3,500-year-old roots of Brit Milah and why the eighth day holds the weight that it does.',
    },
    {
      href: '/testimonials',
      kicker: 'Families',
      title: 'Testimonials',
      blurb:
        'Notes from local families and rabbis who I have had the privilege to work with over the years.',
    },
    {
      href: '/bris',
      kicker: 'Meaning',
      title: 'Why Have a Bris',
      blurb:
        'A medical circumcision is not a bris. A short note on what makes the ritual meaningful.',
    },
    {
      href: '/interfaith',
      kicker: 'Together',
      title: 'Interfaith Couples',
      blurb:
        'Common questions about bris, baby naming, and raising a Jewish child in an interfaith family.',
    },
    {
      href: '/after',
      kicker: 'Healing',
      title: 'After the Bris',
      blurb:
        'Care instructions for the seven days that follow, and what counts as normal vs. worth a call.',
    },
    {
      href: '/contact',
      kicker: 'Reach out',
      title: 'Contact',
      blurb:
        'Phone, email, and address — get in touch to plan your ceremony or ask a question.',
    },
  ]

  return (
    <section className="border-t border-hairline bg-surface">
      <div className="mx-auto max-w-5xl px-6 py-20 md:py-14">
        <div className="mb-12 md:mb-8">
          <p className="text-xs uppercase tracking-widest text-sage">
            Explore the site
          </p>
          <h2 className="mt-2 font-serif text-3xl text-ink sm:text-2xl">
            Everything you need, in one place.
          </h2>
        </div>

        <div className="grid grid-cols-3 gap-6 md:grid-cols-2 sm:grid-cols-1 md:gap-4">
          {cards.map(({ blurb, href, kicker, title }, i) => (
            <Link
              key={href}
              className="group flex flex-col overflow-hidden rounded-xl border border-hairline bg-cream shadow-card transition hover:-translate-y-0.5 hover:border-gold"
              href={href}
            >
              <div className={`h-2 w-full ${tints[i % tints.length]}`} />
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs uppercase tracking-widest text-sage">
                  {kicker}
                </p>
                <h3
                  dangerouslySetInnerHTML={{ __html: title }}
                  className="mt-2 font-serif text-xl text-ink"
                />
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {blurb}
                </p>
                <span className="mt-5 text-sm font-medium text-petrol">
                  Read more →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

const QuoteBand = (): JSX.Element => {
  return (
    <section className="border-t border-hairline bg-cream">
      <div className="mx-auto max-w-3xl px-6 py-20 text-center md:py-14">
        <p className="font-serif text-2xl italic leading-relaxed text-ink sm:text-xl">
          &ldquo;A bris is a sacred moment for a family. My goal is to make it a
          calm and meaningful experience for everyone involved.&rdquo;
        </p>
        <p className="mt-6 text-sm uppercase tracking-widest text-sage">
          — Robert Karasov, M.D.
        </p>
      </div>
    </section>
  )
}

const SiteFooter = (): JSX.Element => {
  return (
    <footer className="bg-petrol text-cream">
      <div className="mx-auto grid max-w-5xl grid-cols-3 gap-10 px-6 py-12 md:grid-cols-1">
        <div>
          <p className="font-serif text-lg">Robert Karasov, M.D.</p>
          <p className="mt-2 text-sm text-cream/70">
            Pediatrician &amp; Mohel · Twin Cities
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-cream/60">
            Site
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link className="hover:text-gold" href="/ceremony">
                The Ceremony
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/tradition">
                Tradition
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/testimonials">
                Testimonials
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/contact">
                Contact
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-cream/60">
            More
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link className="hover:text-gold" href="/bris">
                Why have a bris
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/interfaith">
                Interfaith couples
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/after">
                After the bris
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-cream/10">
        <p className="mx-auto max-w-5xl px-6 py-4 text-xs text-cream/60">
          © Robert Karasov {new Date().getFullYear()}
        </p>
      </div>
    </footer>
  )
}
